import { FILE_TYPE } from '@frontend/constants/file-image-types';
import { isExternalAsset, removeUrlOriginLocation } from '@frontend/utils';
import { htmlIsEmpty } from '@frontend/utils/sanitizeHtml';
import {
  NON_RENDERABLE_IMAGE_EXTENSIONS,
  NON_RENDERABLE_IMAGE_MIMES,
  collectFilesFromDrop,
  collectFilesFromInput,
  downloadUrlAsFile,
  fullFileNameFromUrl,
  isImageFile,
  isVideoFile,
  newPendingAttachment,
  objectUrlForFile,
  pendingAttachmentLabel,
  referenceChipIconClass,
  referenceExtension,
  referenceFileIconMarkup,
  referenceFileType,
  revokePendingPreview,
} from '@frontend/utils/fileAttachments';

// Re-exported so the brief modules keep their single import site.
export {
  collectFilesFromDrop,
  collectFilesFromInput,
  downloadUrlAsFile,
  fullFileNameFromUrl,
  isImageFile,
  isVideoFile,
  newPendingAttachment,
  objectUrlForFile,
  pendingAttachmentLabel,
  referenceChipIconClass,
  referenceExtension,
  referenceFileIconMarkup,
  referenceFileType,
  revokePendingPreview,
};

export function pendingAttachmentToRef(item, { uploading = false } = {}) {
  if (!item) {
    return null;
  }
  const pdpSlot = item.pdpSlot || null;
  const sectionType = item.sectionType || null;
  const markup = {};
  if (pdpSlot) {
    markup.pdp_slot = pdpSlot;
  }
  if (sectionType) {
    markup.section_type = sectionType;
  }
  return {
    id: item.id,
    name: item.name,
    url: item.previewUrl,
    mime: item.mime,
    kind: item.kind,
    markup,
    pdp_slot: pdpSlot,
    section_type: sectionType,
    uploading,
  };
}

// The server resolves the placeholders post-upload; items whose embed was deleted are dropped.
function replaceInlinePendingSrcs(html, pending) {
  let content = html || '';
  const kept = (pending || []).filter((item) => {
    if (!item || !item.inline) {
      return true;
    }
    const parts = item.inlineSrc ? content.split(item.inlineSrc) : [content];
    if (parts.length === 1) {
      return false;
    }
    content = parts.join(`inline-ref://${item.id}`);
    return true;
  });
  return { content, pending: kept };
}

export function prepareBriefCreateDraft(html, pendingItems) {
  const { content, pending } = replaceInlinePendingSrcs(html, pendingItems);
  if (htmlIsEmpty(content)) {
    return { content: pending.length ? 'Attached files' : '', pending };
  }
  return { content: content.trim(), pending };
}

export function referencesFromBrief(brief) {
  if (brief && Array.isArray(brief.references)) {
    return brief.references.filter((r) => r && r.id && r.url);
  }
  const payload = brief && brief.payload;
  const refs = payload && Array.isArray(payload.references) ? payload.references : [];
  return refs.filter((r) => r && r.id && r.url);
}

// Inspiration is the untagged reference grid. Creative Direction (and every other
// section) keeps its pictures in the section HTML and in section_meta, often with
// no brief_references row at all — Quill only round-trips the <img>.
export function collectBriefPickerImages(brief) {
  const references = referencesFromBrief(brief);
  const items = [];
  const seenFiles = new Set();
  const seenUrls = new Set();

  const add = ({ url, name, kind, mime, userFileId, id }) => {
    const displayUrl = pickerDisplayUrl(url);
    const key = imageUrlKey(url);
    if (!displayUrl || !key) {
      return;
    }
    if (userFileId && seenFiles.has(String(userFileId))) {
      return;
    }
    if (seenUrls.has(key)) {
      return;
    }

    const refLike = {
      url: displayUrl,
      name: pickerImageName(name, url),
      kind: kind || 'image',
      mime: mime || '',
    };
    if (!isImageReference(refLike)) {
      return;
    }

    if (userFileId) {
      seenFiles.add(String(userFileId));
    }
    seenUrls.add(key);
    items.push({
      id: userFileId || id || key,
      name: refLike.name,
      url: displayUrl,
    });
  };

  references.forEach((ref) => {
    if (!ref || !isImageReference(ref)) {
      return;
    }
    add({
      url: ref.url,
      name: ref.name,
      kind: ref.kind,
      mime: ref.mime,
      userFileId: ref.user_file_id || null,
      id: ref.id,
    });
  });

  sectionImageEmbeds(brief).forEach((embed) => {
    const key = imageUrlKey(embed.url);
    const match = key
      ? references.find((ref) => ref && imageUrlKey(ref.url) === key)
      : null;
    // An <img> in the section is an image even when its reference row was stored as a generic file.
    add({
      url: (match && match.url) || embed.url,
      name: (match && match.name) || embed.name,
      kind: 'image',
      mime: (match && match.mime) || '',
      userFileId: (match && match.user_file_id) || null,
      id: (match && match.id) || null,
    });
  });

  return items;
}

function sectionImageEmbeds(brief) {
  const sections = Array.isArray(brief && brief.sections) ? brief.sections.slice() : [];
  sections.sort((a, b) => (Number(a && a.sort_order) || 0) - (Number(b && b.sort_order) || 0));
  const metaStore = (brief && brief.payload && brief.payload.section_meta) || {};
  const embeds = [];
  const seenTypes = new Set();

  sections.forEach((section) => {
    const typeId = section && (section.type_id || section.typeId);
    if (typeId) {
      seenTypes.add(String(typeId));
    }
    embeds.push(...imageEmbedsFromHtml(section && section.content));
    if (typeId && metaStore[typeId]) {
      embeds.push(...metaImageEmbeds(metaStore[typeId]));
    }
  });

  Object.keys(metaStore).forEach((typeId) => {
    if (!seenTypes.has(String(typeId))) {
      embeds.push(...metaImageEmbeds(metaStore[typeId]));
    }
  });

  return embeds;
}

function imageEmbedsFromHtml(html) {
  if (!html || typeof html !== 'string' || !/<img\b/i.test(html)) {
    return [];
  }
  const embeds = [];
  const tagRe = /<img\b([^>]*)>/gi;
  let match = tagRe.exec(html);
  while (match) {
    const attrs = match[1] || '';
    const src = decodeHtmlEntities(attrValue(attrs, 'src')).trim();
    if (src && !/^(?:blob:|data:|inline-ref:)/i.test(src)) {
      embeds.push({
        url: src,
        name: decodeHtmlEntities(attrValue(attrs, 'alt')).trim(),
      });
    }
    match = tagRe.exec(html);
  }
  return embeds;
}

function metaImageEmbeds(meta) {
  if (!meta || typeof meta !== 'object') {
    return [];
  }
  const embeds = [];
  const push = (url) => {
    if (url && typeof url === 'string' && url.trim()) {
      embeds.push({ url: url.trim(), name: '' });
    }
  };
  push(meta.conceptImageUrl);
  (Array.isArray(meta.pairs) ? meta.pairs : []).forEach((pair) => {
    if (!pair) {
      return;
    }
    push(pair.originalUrl);
    push(pair.afterUrl);
  });
  (Array.isArray(meta.useForAllImageUrls) ? meta.useForAllImageUrls : []).forEach(push);
  return embeds;
}

function attrValue(attrs, name) {
  const re = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`, 'i');
  const match = String(attrs || '').match(re);
  return match ? (match[1] || match[2] || match[3] || '') : '';
}

function decodeHtmlEntities(value) {
  return String(value || '')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');
}

function pickerImageName(name, url) {
  const fromAlt = (name && String(name).trim()) || '';
  const fromUrl = fullFileNameFromUrl(url) || '';
  const ext = referenceExtension({ name: fromAlt });
  // Alt is the filename Quill stored. A dotted caption ("See fig. 2") is not an
  // extension — using it would hide a real image. Fall back to the file name.
  if (fromAlt && (!ext || RENDERABLE_IMAGE_EXTENSIONS.has(ext))) {
    return fromAlt;
  }
  return fromUrl || fromAlt || 'Image';
}

function imageUrlKey(url) {
  const file = shareFilePath(url);
  if (file) {
    return `share:${file}`;
  }
  const trimmed = (url || '').trim();
  if (!trimmed || /^(?:blob:|data:|inline-ref:)/i.test(trimmed)) {
    return '';
  }
  return trimmed;
}

// Editor embeds absolutize the URL and add lightweight/scale params. Keep the
// file query exactly as stored so a "+" in the name is not turned into a space.
function pickerDisplayUrl(url) {
  const trimmed = (url || '').trim();
  if (!trimmed || /^(?:blob:|data:|inline-ref:)/i.test(trimmed)) {
    return '';
  }
  const rawFile = shareFileQuery(trimmed);
  if (rawFile) {
    return `/share?file=${rawFile}`;
  }
  return trimmed.includes('?') ? trimmed : `${trimmed}?`;
}

function shareFileQuery(url) {
  const trimmed = (url || '').trim();
  if (!trimmed) {
    return '';
  }
  const pathAndQuery = trimmed.replace(/^https?:\/\/[^/]+/i, '');
  const match = pathAndQuery.match(/(?:^|\/)share\?file=([^&#]*)/i);
  return match ? match[1] : '';
}

function shareFilePath(url) {
  const raw = shareFileQuery(url);
  if (!raw) {
    return '';
  }
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function pdpSlotIndexFromRef(ref) {
  if (!ref) {
    return null;
  }
  const raw = ref.pdp_slot ?? (ref.markup && ref.markup.pdp_slot);
  const slot = parseInt(raw, 10);
  return Number.isFinite(slot) && slot >= 1 ? slot : null;
}

export function sectionTypeFromRef(ref) {
  if (!ref) {
    return null;
  }
  return ref.section_type ?? (ref.markup && ref.markup.section_type) ?? null;
}

// References store a relative url; embeds are absolutized before insertEmbed, so compare only
// after stripping origin — the server's siteUrl() origin isn't always window.location's.
function normalizeReferenceUrl(url) {
  return url && typeof url === 'string' ? removeUrlOriginLocation(url) : '';
}

export function inlineReferenceForUrl(references, url) {
  const target = normalizeReferenceUrl(url);
  if (!target) {
    return null;
  }
  return (references || []).find(
    (ref) => ref && ref.inline && normalizeReferenceUrl(ref.url) === target,
  ) || null;
}

export function sameReferenceUrl(a, b) {
  const left = normalizeReferenceUrl(a);
  return !!left && left === normalizeReferenceUrl(b);
}

// Untagged: not inline (those live in the body HTML) and not section-scoped — the global pool.
export function isUntaggedReference(ref) {
  return !!ref && !ref.inline && !sectionTypeFromRef(ref);
}

// Inline images live in the body HTML, so they're skipped here rather than chipped.
export function groupReferencesBySection(references) {
  const map = {};
  (references || []).forEach((ref) => {
    if (!ref || ref.inline) {
      return;
    }
    const type = sectionTypeFromRef(ref);
    if (!type) {
      return;
    }
    (map[type] || (map[type] = [])).push(ref);
  });
  return map;
}

/** Map references onto fixed PDP asset slots using explicit pdp_slot only. */
export function referencesForPdpSlots(refs, slotCount) {
  const slots = Array(slotCount).fill(null);

  (refs || []).forEach((ref) => {
    if (!ref || !ref.id) {
      return;
    }
    const slot = pdpSlotIndexFromRef(ref);
    if (slot != null && slot <= slotCount && !slots[slot - 1]) {
      slots[slot - 1] = ref;
    }
  });

  return slots;
}

export function markupWithPdpSlot(ref, slot) {
  const markup = ref && ref.markup && typeof ref.markup === 'object'
    ? { ...ref.markup }
    : { paths: [], comment: '', color: '#ef4444' };
  markup.pdp_slot = slot;
  return markup;
}

/** Slots each reference moves to once the slot at removedIndex is gone: { [refId]: slot }. */
export function pdpSlotMapAfterRemoval(refs, removedIndex) {
  const slotById = {};
  (refs || []).forEach((ref) => {
    const slot = pdpSlotIndexFromRef(ref);
    if (ref?.id && slot > removedIndex) {
      slotById[ref.id] = slot - 1;
    }
  });
  return slotById;
}

export function invertPdpSlotShift(slotById) {
  const inverted = {};
  Object.keys(slotById).forEach((id) => {
    inverted[id] = slotById[id] + 1;
  });
  return inverted;
}

/**
 * One-time migration: assign pdp_slot to legacy references based on their
 * current display order so deletes do not collapse remaining images.
 * Returns assignments that were applied in-memory.
 */
export function assignMissingPdpSlots(refs, slotCount) {
  const slots = Array(slotCount).fill(null);
  const legacy = [];

  (refs || []).forEach((ref) => {
    if (!ref || !ref.id) {
      return;
    }
    const slot = pdpSlotIndexFromRef(ref);
    if (slot != null && slot <= slotCount) {
      if (!slots[slot - 1]) {
        slots[slot - 1] = ref;
      }
      return;
    }
    if (isUntaggedReference(ref)) {
      legacy.push(ref);
    }
  });

  const assigned = [];
  let legacyIndex = 0;
  for (let i = 0; i < slotCount && legacyIndex < legacy.length; i += 1) {
    if (!slots[i]) {
      const ref = legacy[legacyIndex];
      legacyIndex += 1;
      const slotNum = i + 1;
      if (!pdpSlotIndexFromRef(ref)) {
        ref.markup = markupWithPdpSlot(ref, slotNum);
        ref.pdp_slot = slotNum;
        assigned.push({ ref, slot: slotNum });
      }
      slots[i] = ref;
    }
  }

  return assigned;
}

// Layout snapshots sometimes omit mime and default kind to "image". A PDF named
// `brief.pdf` must still never land in an <img> — browsers show a broken glyph.
const RENDERABLE_IMAGE_EXTENSIONS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif', 'bmp',
]);
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'webm', 'avi']);

/** Max edge for proof/sidebar reference tiles (~100–130px CSS, 2x). */
export const REFERENCE_THUMBNAIL_SIZE = 256;

/**
 * Downscale a /share image URL for thumbnail display. Lightbox/download keep
 * the original; this only adds lightweight+scale+format when missing.
 */
export function imageThumbnailUrl(url, size = REFERENCE_THUMBNAIL_SIZE, format = 'avif') {
  if (!url || typeof url !== 'string') {
    return url || '';
  }
  const trimmed = url.trim();
  if (!trimmed || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed;
  }
  if (trimmed.includes('/share?') || trimmed.includes('/node-share?')) {
    if (trimmed.includes('lightweight=') || trimmed.includes('scale=')) {
      return trimmed;
    }
    const separator = trimmed.includes('?') ? '&' : '?';
    return `${trimmed}${separator}lightweight=1&scale=${size}&format=${format}`;
  }
  return trimmed;
}

export function referenceThumbnailUrl(ref, size = REFERENCE_THUMBNAIL_SIZE) {
  if (!ref) {
    return '';
  }
  if (ref.thumbnail_url) {
    return ref.thumbnail_url;
  }
  return imageThumbnailUrl(ref.url, size);
}

export function isImageReference(ref) {
  if (!ref || !ref.url) {
    return false;
  }
  const ext = referenceExtension(ref);
  if (NON_RENDERABLE_IMAGE_EXTENSIONS.has(ext)) {
    return false;
  }
  if (ext && !RENDERABLE_IMAGE_EXTENSIONS.has(ext)) {
    return false;
  }
  const mime = ((ref && ref.mime) || '').toLowerCase();
  if (NON_RENDERABLE_IMAGE_MIMES.has(mime)) {
    return false;
  }
  if (ref.kind === 'image') {
    return true;
  }
  if (mime.startsWith('image/')) {
    return true;
  }
  return RENDERABLE_IMAGE_EXTENSIONS.has(ext);
}

export function isVideoReference(ref) {
  const ext = referenceExtension(ref);
  if (VIDEO_EXTENSIONS.has(ext)) {
    return true;
  }
  const kind = ref && ref.kind;
  if (kind === 'video') {
    return true;
  }
  const mime = (ref && ref.mime) || '';
  return mime.startsWith('video/');
}

export const MARKUP_VIEW_BOX = '0 0 1000 1000';
export const MARKUP_STROKE_WIDTH = 6;

export function markupPathsFromReference(ref) {
  const markup = ref && ref.markup;
  if (!markup || typeof markup !== 'object' || !Array.isArray(markup.paths)) {
    return [];
  }
  return markup.paths.filter((path) => path && path.d);
}

/** Custom DnD type so Inspiration tiles can be dropped without re-uploading. */
export const BRIEF_REFERENCE_DRAG_TYPE = 'application/x-brief-reference';
const BRIEF_REFERENCE_DRAG_PREFIX = 'brief-reference:';

function dataTransferTypes(dataTransfer) {
  if (!dataTransfer || !dataTransfer.types) {
    return [];
  }
  return Array.from(dataTransfer.types);
}

export function hasBriefReferenceDrag(event) {
  return dataTransferTypes(event && event.dataTransfer).includes(BRIEF_REFERENCE_DRAG_TYPE);
}

function briefReferencePayload(ref) {
  if (!ref || !ref.id || !ref.url) {
    return null;
  }
  return {
    id: ref.id,
    url: ref.url,
    name: ref.name || '',
    kind: ref.kind
      || (isVideoReference(ref) ? 'video' : (isImageReference(ref) ? 'image' : 'file')),
  };
}

// One reference, or a list when the sidebar drags a multi-selection onto the canvas.
export function setBriefReferenceDragData(dataTransfer, refOrRefs) {
  if (!dataTransfer) {
    return false;
  }
  const refs = Array.isArray(refOrRefs) ? refOrRefs : [refOrRefs];
  const payloads = refs.map(briefReferencePayload).filter(Boolean);
  if (!payloads.length) {
    return false;
  }
  const encoded = JSON.stringify(payloads.length === 1 ? payloads[0] : payloads);
  dataTransfer.setData(BRIEF_REFERENCE_DRAG_TYPE, encoded);
  // Safari historically ignores custom MIME types; prefix keeps text/plain non-JSON so
  // file-browser drops that JSON.parse(text/plain) won't treat the reference id as a user_file_id.
  dataTransfer.setData('text/plain', `${BRIEF_REFERENCE_DRAG_PREFIX}${encoded}`);
  dataTransfer.effectAllowed = 'copy';
  return true;
}

function briefReferencePayloadsFromRaw(raw) {
  if (!raw) {
    return [];
  }
  try {
    const payload = JSON.parse(raw);
    const list = Array.isArray(payload) ? payload : [payload];
    return list.filter((item) => item && item.id && item.url);
  } catch {
    return [];
  }
}

function readBriefReferenceDragRaw(event) {
  const dataTransfer = event && event.dataTransfer;
  if (!dataTransfer) {
    return '';
  }
  let raw = '';
  try {
    raw = dataTransfer.getData(BRIEF_REFERENCE_DRAG_TYPE) || '';
  } catch {
    raw = '';
  }
  if (raw) {
    return raw;
  }
  try {
    const plain = dataTransfer.getData('text/plain') || '';
    if (plain.startsWith(BRIEF_REFERENCE_DRAG_PREFIX)) {
      return plain.slice(BRIEF_REFERENCE_DRAG_PREFIX.length);
    }
  } catch {
    return '';
  }
  return '';
}

export function parseBriefReferenceDragList(event) {
  return briefReferencePayloadsFromRaw(readBriefReferenceDragRaw(event));
}

export function parseBriefReferenceDrag(event) {
  return parseBriefReferenceDragList(event)[0] || null;
}

// Matches BriefContentPanel's default cap so the jump chip counts the images on screen.
export const BRIEF_SIDEBAR_REFERENCE_LIMIT = 9;

export function sidebarImageReferences(brief, limit = BRIEF_SIDEBAR_REFERENCE_LIMIT) {
  const visible = referencesFromBrief(brief).filter((ref) => ref && ref.url);
  const capped = limit > 0 ? visible.slice(0, limit) : visible;
  return capped.filter((ref) => isImageReference(ref));
}

/**
 * Scroll a container when the pointer is near its top/bottom edge during HTML5 drag.
 * Returns true when scroll position changed (so a rAF loop can keep going while held still).
 */
export function scrollContainerAtDragEdge(container, clientY, {
  edge = 72,
  maxSpeed = 28,
} = {}) {
  if (!container || clientY == null) {
    return false;
  }
  const rect = container.getBoundingClientRect();
  if (rect.height <= edge * 2) {
    return false;
  }
  let delta = 0;
  if (clientY < rect.top + edge) {
    const intensity = Math.min(1, (rect.top + edge - clientY) / edge);
    delta = -Math.ceil(maxSpeed * intensity);
  } else if (clientY > rect.bottom - edge) {
    const intensity = Math.min(1, (clientY - (rect.bottom - edge)) / edge);
    delta = Math.ceil(maxSpeed * intensity);
  }
  if (!delta) {
    return false;
  }
  const before = container.scrollTop;
  container.scrollTop = before + delta;
  return container.scrollTop !== before;
}

/** Payload shape for POST /briefs/{id}/references/import — one contract, several pickers. */
export function externalImportAsset(item) {
  return {
    name: item.name,
    path: item.original_path || item.path,
    extension: item.extension || null,
  };
}

// Products ids are numeric but belong to another table, so they must never reach the link endpoint.
const LIBRARY_ASSET_TYPE = 'file_asset';

/**
 * Split a file-browser (SelectFolderModal) selection by how each item reaches the
 * brief: library files are linked by id, external DAM / stock assets are imported by
 * path, and anything else can't be attached from here.
 */
export function splitLibrarySelection(items) {
  const userFileIds = [];
  const importable = [];
  let unsupported = 0;

  (Array.isArray(items) ? items : [])
    .filter((item) => item && item.type !== FILE_TYPE.FOLDER && item.id != null)
    .forEach((item) => {
      const id = Number(item.id);
      const path = item.original_path || item.path;

      if (item.asset_type === LIBRARY_ASSET_TYPE && Number.isInteger(id) && id > 0) {
        userFileIds.push(id);
      } else if (isExternalAsset(item) && path) {
        importable.push(externalImportAsset(item));
      } else {
        unsupported += 1;
      }
    });

  return { userFileIds, importable, unsupported };
}

/** markup.section_type of a PDP brief's "Product images" (BriefReference::PRODUCT_IMAGES_SECTION). */
export const PRODUCT_IMAGES_SECTION_TYPE = 'product_images';

export function isProductImageReference(ref) {
  return !!ref && !ref.inline && sectionTypeFromRef(ref) === PRODUCT_IMAGES_SECTION_TYPE;
}

/** A PDP slot image marked "As-is": it IS that tile, not a reference for it. (POR-14794) */
export function isAsIsReference(ref) {
  return !!ref && !!(ref.as_is ?? (ref.markup && ref.markup.as_is));
}

function httpUrl(value) {
  if (!/^https?:\/\//i.test(value || '')) {
    return null;
  }
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

/**
 * An ASIN or an Amazon product page typed into "Product images" → what the Amazon
 * image lookup takes ({ query, asin }), or null for anything else. Amazon's image CDN
 * (m.media-amazon.com) is a plain image URL, not a product page. (POR-14794)
 */
export function amazonProductQuery(value) {
  const text = String(value || '').trim();
  if (/^[A-Z0-9]{10}$/i.test(text)) {
    return { query: text.toUpperCase(), asin: text.toUpperCase() };
  }
  const url = httpUrl(text);
  if (!url || !/(^|\.)amazon\.[a-z.]+$/i.test(url.hostname)) {
    return null;
  }
  const match = text.match(/\/(?:dp|gp\/product|gp\/aw\/d|product-reviews)\/([A-Z0-9]{10})(?![A-Z0-9])/i)
    || text.match(/[?&]asin=([A-Z0-9]{10})(?![A-Z0-9])/i);
  return { query: text, asin: match ? match[1].toUpperCase() : null };
}

/** An image URL typed into "Product images" → an import asset, or null when it isn't an http(s) URL. */
export function imageUrlAsset(value) {
  const text = String(value || '').trim();
  const url = httpUrl(text);
  if (!url) {
    return null;
  }
  let name = url.pathname.split('/').pop() || '';
  try {
    name = decodeURIComponent(name);
  } catch {
    // Malformed escape — keep the raw segment.
  }
  return { name: name.slice(0, 255) || null, path: text, extension: null };
}
